import React, { useRef } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  IconButton,
  useTheme
} from '@mui/material';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import { BusinessImages } from '../services/bookingService';

interface BusinessAboutSectionProps {
  businessName: string;
  logoUrl?: string | null;
  businessDescription?: string;
  businessImages?: BusinessImages[];
}

export const BusinessAboutSection: React.FC<BusinessAboutSectionProps> = ({
  businessName,
  logoUrl,
  businessDescription,
  businessImages
}) => {
  const theme = useTheme();
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const hasImages = (businessImages?.length ?? 0) > 0;
  const scrollBy = (dx: number) => scrollerRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  return (
    <Box id="about" sx={{ scrollMarginTop: 80 }}>
      <Box maxWidth="1100px" mx="auto" px={{ xs: 2, sm: 3 }} py={{ xs: 3, sm: 4 }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(22,22,22,0.85)'
                : 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(6px)'
          }}
        >
          {/* Header — larger logo + matching title weight; no ring/underline/gradient */}
          <Stack direction="row" alignItems="center" spacing={{ xs: 2, sm: 3 }} sx={{ mb: 1 }}>

            {/* Logo */}
            {logoUrl && logoUrl.trim() !== '' && (
            <Box
              sx={{
                width: { xs: 84, sm: 96, md: 108 },
                height: { xs: 84, sm: 96, md: 108 },
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
                flex: '0 0 auto'
              }}
            >
              <img
                src={logoUrl}
                alt={`${businessName} logo`}
                loading="lazy"
                style={{ width: '82%', height: '82%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          )}

            {/* Business Name */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 34, sm: 40, md: 48 }, // big but not overflowing
                  fontWeight: 900,
                  lineHeight: 1.1,
                  color: 'text.primary',
                  letterSpacing: -0.5,
                  wordBreak: 'break-word' // wrap long names
                }}
              >
                {businessName}
              </Typography>
              {/* subtle accent bar to anchor the header (uses primary color) */}
              <Box sx={{ mt: 1, height: 3, width: 72, borderRadius: 2, bgcolor: 'primary.main' }} />
            </Box>
          </Stack>

          {/* About */}
          { businessDescription && (
            <Box mt={2.5}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: 'secondary.main', mb: 0.75, letterSpacing: 0.6 }}
              >
                About
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                {businessDescription || 'Business description coming soon.'}
              </Typography>
            </Box>
          )}

          {/* Photos */}
          {hasImages && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={3.5} mb={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              Photos
            </Typography>
            {hasImages && (
              <Stack direction="row" spacing={1}>
                <IconButton aria-label="Scroll left" size="small" onClick={() => scrollBy(-300)}>
                  <ArrowBackIosNew fontSize="small" />
                </IconButton>
                <IconButton aria-label="Scroll right" size="small" onClick={() => scrollBy(300)}>
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Stack>
          )}

          {/* Horizontal rail with subtle edge fades */}
          <Box
            position="relative"
            sx={{
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: 36,
                zIndex: 2,
                pointerEvents: 'none'
              },
              // '&::before': {
              //   left: -8,
              //   background: `linear-gradient(90deg, ${theme.palette.background.paper} 0%, rgba(0,0,0,0) 100%)`
              // },
              // '&::after': {
              //   right: -8,
              //   background: `linear-gradient(270deg, ${theme.palette.background.paper} 0%, rgba(0,0,0,0) 100%)`
              // }
            }}
          >
            <Box
              ref={scrollerRef}
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                py: 1,
                px: 0.5,
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {hasImages && (
                businessImages!.map((image, i) => (
                  <Box
                    key={image.imageUrl || `img-${i}`}
                    sx={{
                      flex: '0 0 auto',
                      width: { xs: 200, sm: 240, md: 280 },
                      aspectRatio: '4 / 3',
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      // border: `1px solid ${theme.palette.divider}`,
                      backgroundImage: `url(${image.imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      scrollSnapAlign: 'start'
                    }}
                    aria-label={`Business image ${i + 1}`}
                    role="img"
                  />
                ))
              )}
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
